import React, { useState, useRef, useCallback, useEffect } from 'react';
import Webcam from 'react-webcam';
import {
  Box,
  Grid,
  Paper,
  Typography,
  useMediaQuery,
  Container,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
  Autocomplete,
  Tooltip,
  Chip,
} from '@mui/material';
import VerifiedIcon from '@mui/icons-material/Verified';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BusinessIcon from '@mui/icons-material/Business';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import SchoolIcon from '@mui/icons-material/School';
import BadgeIcon from '@mui/icons-material/Badge';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import Replay from '@mui/icons-material/Replay';
import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';

import axios from 'axios';

import Sidebar from '../../../../components/sidebars/Sidebar_HR';
import TopNav from '../../../../components/TopNav_hotelStaff';

import {
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  FormHelperText,
} from '@mui/material';
import { InputLabel, MenuItem, Select } from '@mui/material';
import { useNavigate } from 'react-router-dom';

const steps = [
  'Personal Info',
  'Additional Details',
  'Capture',
  'Education',
  'Experience',
  'Last Employer',
  'Uploads & Security',
  'Salary Account'
];

const initialData = {
  personalInfo: {
    date_of_birth: '',
    marital_status: '',
    country: 'Nigeria',
    state_of_origin: '',
    lga: '',
    residential_address: '',
    hometown_permanent_address: '',
    phone: '',
    status: 'Active',
  },
  jobDetails: {
    health_status: '',
    id_type: '',
    id_number: '',
    next_of_kin_name: '',
    relationship: '',
    next_of_kin_address: '',
    next_of_kin_contact: '',
    status: 'Active',
  },
  capture: {
    image: null,
    status: 'Active',
  },
  education: {
    primary_school_name: '',
    primary_school_address: '',
    secondary_school_name: '',
    secondary_school_address: '',
    tertiary_school_name: '',
    tertiary_school_address: '',
    status: 'Active',
  },
  experience: {
    experience_type: '',
    years_of_experience: '',
    last_workplace_one: '',
    last_workplace_two: '',
    self_evaluation: '',
    salary_expectation: '',
    status: 'Active',
  },
  employment: {
    previous_employer: '',
    job_title: '',
    employment_start_date: '',
    employment_end_date: '',
    reason_for_leaving: '',
    employer_contact_name: '',
    employer_phone: '',
    status: 'Active',
  },
  security_and_upload: {
    guarantor_upload: null,
    staff_signature_upload: null,
    cv_upload: null,
    academic_result_upload: null,
    id_upload: null,
    additional_academic_uploads: [],
    convicted: false,
    security_declaration: false,
    status: 'Active',
  },
  bank: {
    account_number: '',
    account_name: '',
    bank_name: '',
    bank_code: '',
    account_verified: false,
    status: 'Active',
  }
};

// Validation rules
// Validation rules - UPDATE the experience section
const validationRules = {
  personalInfo: {
    date_of_birth: { required: true, pattern: /^\d{4}-\d{2}-\d{2}$/, message: 'Valid date of birth is required' },
    marital_status: { required: true, message: 'Marital status is required' },
    country: { required: true, message: 'Country is required' },
    state_of_origin: { required: true, message: 'State of origin is required' },
    lga: { required: true, message: 'LGA is required' },
    residential_address: { required: true, message: 'Residential address is required' },
    hometown_permanent_address: { required: true, message: 'Hometown address is required' },
  },
  jobDetails: {
    health_status: { required: true, message: 'Health status is required' },
    id_type: { required: true, message: 'ID type is required' },
    id_number: { required: true, pattern: /^[A-Za-z0-9]{5,20}$/, message: 'Valid ID number is required (5-20 characters)' },
    next_of_kin_name: { required: true, message: 'Next of kin name is required' },
    relationship: { required: true, message: 'Relationship is required' },
    next_of_kin_address: { required: true, message: 'Next of kin address is required' },
    next_of_kin_contact: { required: true, pattern: /^\d{10,15}$/, message: 'Valid phone number is required' },
  },
  education: {
    primary_school_name: { required: true, message: 'Primary school name is required' },
    primary_school_address: { required: true, message: 'Primary school address is required' },
  },
  experience: {
    experience_type: { required: true, message: 'Experience type is required' },
    // years_of_experience - NOT required anymore
    self_evaluation: { required: true, message: 'Self evaluation is required' },
    salary_expectation: { required: true, pattern: /^\d+$/, message: 'Valid salary expectation is required' },
  },
  employment: {
    previous_employer: { required: false, message: '' },
    job_title: { required: false, message: '' },
    employment_start_date: { required: false, message: '' },
    reason_for_leaving: { required: false, message: '' },
    employer_contact_name: { required: false, message: '' },
    employer_phone: { required: false, message: '' },
  },
};
// Experience type options with "No Experience"
const experienceTypeOptions = [
  { value: 'no_experience', label: 'No Experience (Entry Level / Fresh Graduate)' },
  { value: 'internship', label: 'Internship Experience' },
  { value: 'entry_level', label: 'Entry Level (0-2 years)' },
  { value: 'junior', label: 'Junior Level (2-4 years)' },
  { value: 'mid_level', label: 'Mid Level (4-7 years)' },
  { value: 'senior', label: 'Senior Level (7-10 years)' },
  { value: 'lead', label: 'Lead / Supervisor (10+ years)' },
  { value: 'manager', label: 'Manager Level' },
  { value: 'director', label: 'Director / Executive Level' },
];

const HRFormDashboard = () => {
  const [banks, setBanks] = useState([]);
  const [verifyingAccount, setVerifyingAccount] = useState(false);
  const [banksLoading, setBanksLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState(initialData);
  const [completed, setCompleted] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:768px)');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const toggleSidebar = () => setSidebarOpen((prev) => !prev);
  const [userData, setUserData] = useState(null);
  const webcamRef = useRef(null);
  const [imageSrc, setImageSrc] = useState(null);
 
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [errorDialog, setErrorDialog] = useState({
    open: false,
    title: '',
    message: '',
    details: [],
    fieldErrors: {}
  });

  const capture = useCallback(() => {
    const image = webcamRef.current.getScreenshot();
    if (!image) {
      showSnackbar('Failed to capture image', 'error');
      return;
    }
    setImageSrc(image);
    showSnackbar('Photo captured successfully', 'success');
  }, [webcamRef]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const validateField = (section, field, value) => {
    const rules = validationRules[section]?.[field];
    if (!rules) return true;
    
    // Skip validation if field is not required
    if (!rules.required) return true;

    if (rules.required && (!value || value.toString().trim() === '')) {
      return false;
    }

    if (rules.pattern && value && !rules.pattern.test(value.toString())) {
      return false;
    }

    return true;
  };

  const validateStepFields = (step) => {
    const sectionMap = {
      0: 'personalInfo',
      1: 'jobDetails',
      3: 'education',
      4: 'experience',
      5: 'employment',
    };

    const section = sectionMap[step];
    if (!section) return true;

    const rules = validationRules[section];
    if (!rules) return true;

    const sectionData = formData[section];
    const newErrors = {};
    let isValid = true;

    Object.keys(rules).forEach(field => {
      const rule = rules[field];
      // Only validate required fields
      if (rule.required) {
        const value = sectionData[field];
        if (!validateField(section, field, value)) {
          newErrors[field] = rule.message;
          isValid = false;
        }
      }
    });

    setFieldErrors(prev => ({ ...prev, [section]: newErrors }));
    return isValid;
  };

  const handleFieldBlur = (section, field) => {
    setTouchedFields(prev => ({
      ...prev,
      [section]: { ...prev[section], [field]: true }
    }));
    validateField(section, field, formData[section][field]);
  };

  const handleNext = async () => {
    // Special validation for step 6 (Uploads & Security)
    if (activeStep === 6) {
      if (!formData.security_and_upload.security_declaration) {
        showSnackbar('You must agree to the security declaration', 'error');
        return;
      }
      if (!formData.security_and_upload.guarantor_upload) {
        showSnackbar('Guarantor upload is required', 'error');
        return;
      }
      if (!formData.security_and_upload.cv_upload) {
        showSnackbar('CV upload is required', 'error');
        return;
      }
      if (!formData.security_and_upload.academic_result_upload) {
        showSnackbar('Academic result upload is required', 'error');
        return;
      }
      if (!formData.security_and_upload.id_upload) {
        showSnackbar('ID upload is required', 'error');
        return;
      }
      if (!formData.security_and_upload.staff_signature_upload) {
        showSnackbar('Staff signature upload is required', 'error');
        return;
      }
    }

    // Special validation for step 7 (Salary Account)
    if (activeStep === 7) {
      if (!formData.bank.account_verified) {
        showSnackbar('Please verify your bank account before submitting', 'error');
        return;
      }
      if (!formData.bank.account_number) {
        showSnackbar('Account number is required', 'error');
        return;
      }
      if (!formData.bank.bank_code) {
        showSnackbar('Please select a bank', 'error');
        return;
      }
    }

    // Validate current step fields
    if (!validateStepFields(activeStep)) {
      showSnackbar('Please fix all errors before proceeding', 'error');
      return;
    }

    const success = await submitStep(activeStep);

    if (success) {
      setCompleted({ ...completed, [activeStep]: true });

      if (activeStep < steps.length - 1) {
        setActiveStep((prev) => prev + 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        showSnackbar('Onboarding completed successfully! Redirecting to dashboard...', 'success');
        setTimeout(() => {
          navigate('/hotel/HR/Dashboard_hr');
        }, 3000);
      }
    }
  };

  const fetchUserData = useCallback(async () => {
    try {
      const phone = localStorage.getItem('phoneNumber');
      if (!phone) {
        showSnackbar('Phone number not found in session', 'error');
        setTimeout(() => navigate('/hotel/HR/Dashboard_hr'), 2000);
        return;
      }

      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/hotel/HR/get_userstable.php`, {
        params: { phone }
      });

      if (response.data.success) {
        setUserData(response.data.data);
        setFormData(prev => ({
          ...prev,
          personalInfo: { ...prev.personalInfo, phone }
        }));
      } else {
        showSnackbar(response.data.message || 'User not found', 'error');
        navigate('/hotel/HR/Dashboard_hr');
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      showSnackbar('Failed to fetch user data', 'error');
      navigate('/hotel/HR/Dashboard_hr');
    }
  }, [navigate]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  // Fetch banks on component mount
  useEffect(() => {
    const fetchBanks = async () => {
      setBanksLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/hotel/HR/verify_bank_account.php?action=get_banks`
        );
        if (response.data.success) {
          setBanks(response.data.data);
        }
      } catch (err) {
        console.error('Error fetching banks:', err);
        setBanks([
          { code: '001', name: 'Access Bank' },
          { code: '002', name: 'Citibank' },
          { code: '003', name: 'Ecobank' },
          { code: '004', name: 'Fidelity Bank' },
          { code: '005', name: 'First Bank of Nigeria' },
          { code: '006', name: 'First City Monument Bank' },
          { code: '007', name: 'Guaranty Trust Bank' },
          { code: '008', name: 'Heritage Bank' },
          { code: '009', name: 'Keystone Bank' },
          { code: '010', name: 'Polaris Bank' },
          { code: '011', name: 'Providus Bank' },
          { code: '012', name: 'Stanbic IBTC Bank' },
          { code: '013', name: 'Standard Chartered Bank' },
          { code: '014', name: 'Sterling Bank' },
          { code: '015', name: 'Suntrust Bank' },
          { code: '016', name: 'Union Bank of Nigeria' },
          { code: '017', name: 'United Bank for Africa' },
          { code: '018', name: 'Unity Bank' },
          { code: '019', name: 'Wema Bank' },
          { code: '020', name: 'Zenith Bank' },
        ]);
      } finally {
        setBanksLoading(false);
      }
    };
    
    fetchBanks();
  }, []);

  const handleStep = (index) => {
    setActiveStep(index);
    setFieldErrors({});
  };

  const handleChange = (section, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
    
    if (fieldErrors[section]?.[field]) {
      setFieldErrors(prev => ({
        ...prev,
        [section]: { ...prev[section], [field]: undefined }
      }));
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleCloseErrorDialog = () => {
    setErrorDialog(prev => ({ ...prev, open: false }));
  };

  const addAdditionalAcademicDocument = () => {
    if (formData.security_and_upload.additional_academic_uploads.length >= 3) {
      showSnackbar('Maximum of 3 additional academic documents allowed', 'warning');
      return;
    }
    
    const newDoc = {
      id: Date.now(),
      document_name: '',
      file: null
    };
    setFormData((prev) => ({
      ...prev,
      security_and_upload: {
        ...prev.security_and_upload,
        additional_academic_uploads: [
          ...prev.security_and_upload.additional_academic_uploads,
          newDoc
        ]
      }
    }));
  };

  const removeAdditionalAcademicDocument = (id) => {
    setFormData((prev) => ({
      ...prev,
      security_and_upload: {
        ...prev.security_and_upload,
        additional_academic_uploads: prev.security_and_upload.additional_academic_uploads.filter(
          doc => doc.id !== id
        )
      }
    }));
  };

  const updateAdditionalDocumentName = (id, name) => {
    setFormData((prev) => ({
      ...prev,
      security_and_upload: {
        ...prev.security_and_upload,
        additional_academic_uploads: prev.security_and_upload.additional_academic_uploads.map(doc =>
          doc.id === id ? { ...doc, document_name: name } : doc
        )
      }
    }));
  };

  const updateAdditionalDocumentFile = (id, file) => {
    setFormData((prev) => ({
      ...prev,
      security_and_upload: {
        ...prev.security_and_upload,
        additional_academic_uploads: prev.security_and_upload.additional_academic_uploads.map(doc =>
          doc.id === id ? { ...doc, file } : doc
        )
      }
    }));
  };

  const submitStep = async (step) => {
    setLoading(true);

    try {
      let payload = {};
      let endpoint = '';
      let successMessage = '';

      if (step === 0) {
        payload = {
          ...formData.personalInfo,
          phone: localStorage.getItem('phoneNumber') || '',
          surname: userData?.first_name || '',
          other_names: userData?.other_names || '',
          email: userData?.email || ''
        };
        endpoint = `${process.env.REACT_APP_API_BASE_URL}/hotel/HR/personalinfo.php`;
        successMessage = 'Personal info saved successfully';

      } else if (step === 1) {
        payload = {
          ...formData.jobDetails,
          phone: localStorage.getItem('phoneNumber') || ''
        };
        endpoint = `${process.env.REACT_APP_API_BASE_URL}/hotel/HR/personalinfo2.php`;
        successMessage = 'Additional info saved successfully';

      } else if (step === 2) {
        if (!imageSrc) {
          throw new Error("Please capture an image first");
        }
        payload = {
          image: imageSrc,
          phone: localStorage.getItem('phoneNumber') || ''
        };
        endpoint = `${process.env.REACT_APP_API_BASE_URL}/hotel/HR/photo_capture.php`;
        successMessage = 'Photo saved successfully';

      } else if (step === 3) {
        payload = {
          ...formData.education,
          phone: localStorage.getItem('phoneNumber') || ''
        };
        endpoint = `${process.env.REACT_APP_API_BASE_URL}/hotel/HR/education.php`;
        successMessage = 'Education info saved successfully';

      } else if (step === 4) {
        payload = {
          ...formData.experience,
          phone: localStorage.getItem('phoneNumber') || ''
        };
        endpoint = `${process.env.REACT_APP_API_BASE_URL}/hotel/HR/job_experience.php`;
        successMessage = 'Experience info saved successfully';

      } else if (step === 5) {
        payload = {
          ...formData.employment,
          phone: localStorage.getItem('phoneNumber') || '',
          status: formData.employment.status || 'Active',
        };
        endpoint = `${process.env.REACT_APP_API_BASE_URL}/hotel/HR/latest_previous_employment.php`;
        successMessage = 'Employment history saved successfully';

      } else if (step === 6) {
        const convertFileToBase64 = (file) => {
          if (!file) return Promise.resolve(null);
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              if (typeof reader.result === 'string' && reader.result.startsWith('data:')) {
                resolve(reader.result);
              } else {
                reject(new Error('Invalid file format'));
              }
            };
            reader.onerror = error => reject(error);
            reader.readAsDataURL(file);
          });
        };

        const validateFileSize = (file) => {
          if (file && file.size > 5 * 1024 * 1024) {
            throw new Error(`File ${file.name} exceeds 5MB limit`);
          }
          return true;
        };

        const mainFiles = [
          formData.security_and_upload.guarantor_upload,
          formData.security_and_upload.cv_upload,
          formData.security_and_upload.academic_result_upload,
          formData.security_and_upload.id_upload,
          formData.security_and_upload.staff_signature_upload
        ];
        
        mainFiles.forEach(file => {
          if (file instanceof File) validateFileSize(file);
        });

        formData.security_and_upload.additional_academic_uploads.forEach(upload => {
          if (upload.file instanceof File) validateFileSize(upload.file);
        });

        const [
          guarantor_upload,
          cv_upload,
          academic_result_upload,
          id_upload,
          staff_signature_upload
        ] = await Promise.all([
          convertFileToBase64(formData.security_and_upload.guarantor_upload),
          convertFileToBase64(formData.security_and_upload.cv_upload),
          convertFileToBase64(formData.security_and_upload.academic_result_upload),
          convertFileToBase64(formData.security_and_upload.id_upload),
          convertFileToBase64(formData.security_and_upload.staff_signature_upload)
        ]);

        const additionalAcademicUploads = await Promise.all(
          formData.security_and_upload.additional_academic_uploads.map(async (upload) => ({
            id: upload.id,
            document_name: upload.document_name,
            file: await convertFileToBase64(upload.file)
          }))
        );

        payload = {
          phone: localStorage.getItem('phoneNumber') || '',
          status: formData.security_and_upload.status || 'Active',
          convicted: formData.security_and_upload.convicted || false,
          security_declaration: formData.security_and_upload.security_declaration || false,
          guarantor_upload,
          cv_upload,
          academic_result_upload,
          id_upload,
          staff_signature_upload,
          additional_academic_uploads: JSON.stringify(additionalAcademicUploads)
        };

        endpoint = `${process.env.REACT_APP_API_BASE_URL}/hotel/HR/security_and_uploads.php`;
        successMessage = 'Documents uploaded successfully';

      } else if (step === 7) {
        if (!formData.bank.account_verified) {
          showSnackbar('Please verify your bank account before submitting', 'error');
          return false;
        }
        
        payload = {
          ...formData.bank,
          phone: localStorage.getItem('phoneNumber') || '',
          status: 'Active',
        };
        endpoint = `${process.env.REACT_APP_API_BASE_URL}/hotel/HR/salary.php`;
        successMessage = 'Bank information saved successfully';
      } else {
        throw new Error(`Unsupported step: ${step}`);
      }

      Object.keys(payload).forEach(key => {
        if (payload[key] === null || payload[key] === undefined) {
          payload[key] = '';
        }
      });

      const response = await axios.post(endpoint, payload, {
        headers: { 'Content-Type': 'application/json' },
        validateStatus: status => status < 500
      });

      if (response.data.success) {
        showSnackbar(successMessage, 'success');
        return true;
      } else {
        if (response.status === 409 || response.data.existing_record) {
          setErrorDialog({
            open: true,
            title: 'Record Already Exists',
            message: response.data.message,
            details: [],
            fieldErrors: {}
          });
        } else {
          const errorDetails = [];
          const fieldErrors = {};
          
          if (response.data.errors) {
            Object.entries(response.data.errors).forEach(([key, value]) => {
              if (key !== 'message' && typeof value === 'string') {
                fieldErrors[key] = value;
                errorDetails.push(`${key}: ${value}`);
              } else if (Array.isArray(value)) {
                errorDetails.push(...value);
              } else if (typeof value === 'string') {
                errorDetails.push(value);
              }
            });
          }
          
          setErrorDialog({
            open: true,
            title: 'Submission Error',
            message: response.data.message || 'Please correct the following issues:',
            details: errorDetails,
            fieldErrors: fieldErrors
          });
        }
        return false;
      }
    } catch (error) {
      console.error('Submission error:', error);
      let errorMessage = 'An unexpected error occurred';
      let errorDetails = [];
      let fieldErrors = {};
      
      if (error.response) {
        errorMessage = error.response.data.message || `Server error (${error.response.status})`;
        if (error.response.data.errors) {
          Object.entries(error.response.data.errors).forEach(([key, value]) => {
            if (key !== 'message') {
              fieldErrors[key] = value;
              errorDetails.push(`${key}: ${value}`);
            }
          });
        }
      } else if (error.request) {
        errorMessage = 'Network error - please check your connection';
        errorDetails = ['Unable to connect to the server'];
      } else {
        errorMessage = error.message;
        errorDetails = [error.message];
      }
      
      setErrorDialog({
        open: true,
        title: 'Submission Failed',
        message: errorMessage,
        details: errorDetails,
        fieldErrors: fieldErrors
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const renderTextField = (section, field, label, type = 'text', required = false, options = {}) => {
    const value = formData[section]?.[field] || '';
    const error = fieldErrors[section]?.[field];
    const isTouched = touchedFields[section]?.[field];
    const showError = error && isTouched;

    return (
      <TextField
        fullWidth
        type={type}
        label={required ? `${label} *` : label}
        value={value}
        onChange={(e) => handleChange(section, field, e.target.value)}
        onBlur={() => handleFieldBlur(section, field)}
        error={!!showError}
        helperText={showError || options.helperText}
        required={required}
        {...options}
      />
    );
  };

  const renderSelectField = (section, field, label, options, required = false) => {
    const value = formData[section]?.[field] || '';
    const error = fieldErrors[section]?.[field];
    const isTouched = touchedFields[section]?.[field];

    return (
      <FormControl fullWidth required={required} error={!!(error && isTouched)}>
        <InputLabel>{label}</InputLabel>
        <Select
          value={value}
          label={label}
          onChange={(e) => handleChange(section, field, e.target.value)}
          onBlur={() => handleFieldBlur(section, field)}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">Select {label}</MenuItem>
          {options.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
        {error && isTouched && <FormHelperText>{error}</FormHelperText>}
      </FormControl>
    );
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" color="primary" gutterBottom>
                Personal Information
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Please provide your personal details accurately
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              {renderTextField('personalInfo', 'date_of_birth', 'Date of Birth', 'date', true, {
                InputLabelProps: { shrink: true },
                helperText: 'Select your date of birth'
              })}
            </Grid>
            
            <Grid item xs={12} md={6}>
              {renderSelectField('personalInfo', 'marital_status', 'Marital Status', [
                { value: 'Single', label: 'Single' },
                { value: 'Married', label: 'Married' },
                { value: 'Divorced', label: 'Divorced' },
                { value: 'Widowed', label: 'Widowed' },
                { value: 'Separated', label: 'Separated' },
              ], true)}
            </Grid>
            
            <Grid item xs={12} md={6}>
              {renderTextField('personalInfo', 'country', 'Country', 'text', true, {
                helperText: 'Your country of residence'
              })}
            </Grid>
            
            <Grid item xs={12} md={6}>
              {renderTextField('personalInfo', 'state_of_origin', 'State of Origin', 'text', true, {
                helperText: 'Your state of origin'
              })}
            </Grid>
            
            <Grid item xs={12} md={6}>
              {renderTextField('personalInfo', 'lga', 'Local Government Area (LGA)', 'text', true, {
                helperText: 'Your local government area'
              })}
            </Grid>
            
            <Grid item xs={12}>
              {renderTextField('personalInfo', 'residential_address', 'Residential Address', 'text', true, {
                multiline: true,
                rows: 2,
                helperText: 'Your current residential address'
              })}
            </Grid>
            
            <Grid item xs={12}>
              {renderTextField('personalInfo', 'hometown_permanent_address', 'Hometown / Permanent Address', 'text', true, {
                multiline: true,
                rows: 2,
                helperText: 'Your permanent address in your hometown'
              })}
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" color="primary" gutterBottom>
                Additional Details
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Please provide additional information about yourself
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              {renderSelectField('jobDetails', 'health_status', 'Health Status', [
                { value: 'Healthy', label: 'Healthy' },
                { value: 'Disabled', label: 'Disabled' },
                { value: 'Chronic Illness', label: 'Chronic Illness' },
                { value: 'Visually Impaired', label: 'Visually Impaired' },
                { value: 'Hearing Impaired', label: 'Hearing Impaired' },
                { value: 'Mental Health Condition', label: 'Mental Health Condition' },
                { value: 'Recovering from Injury', label: 'Recovering from Injury' },
                { value: 'Terminal Illness', label: 'Terminal Illness' },
                { value: 'Other', label: 'Other' },
              ], true)}
            </Grid>
            
            <Grid item xs={12} md={6}>
              {renderSelectField('jobDetails', 'id_type', 'ID Type', [
                { value: 'National ID', label: 'National ID (NIN)' },
                { value: "Voter's Card", label: "Voter's Card (PVC)" },
                { value: "Driver's License", label: "Driver's License" },
                { value: 'International Passport', label: 'International Passport' },
              ], true)}
            </Grid>
            
            <Grid item xs={12} md={6}>
              {renderTextField('jobDetails', 'id_number', 'ID Number', 'text', true, {
                helperText: 'Enter your identification number (5-20 characters)'
              })}
            </Grid>
            
            <Grid item xs={12} md={6}>
              {renderTextField('jobDetails', 'next_of_kin_name', 'Next of Kin Name', 'text', true)}
            </Grid>
            
            <Grid item xs={12} md={6}>
              {renderSelectField('jobDetails', 'relationship', 'Relationship', [
                { value: 'Spouse', label: 'Spouse' },
                { value: 'Parent', label: 'Parent' },
                { value: 'Sibling', label: 'Sibling' },
                { value: 'Child', label: 'Child' },
                { value: 'Uncle', label: 'Uncle' },
                { value: 'Aunt', label: 'Aunt' },
                { value: 'Cousin', label: 'Cousin' },
                { value: 'Friend', label: 'Friend' },
                { value: 'Other', label: 'Other' },
              ], true)}
            </Grid>
            
            <Grid item xs={12}>
              {renderTextField('jobDetails', 'next_of_kin_address', 'Next of Kin Address', 'text', true, {
                multiline: true,
                rows: 2
              })}
            </Grid>
            
            <Grid item xs={12} md={6}>
              {renderTextField('jobDetails', 'next_of_kin_contact', 'Next of Kin Contact', 'tel', true, {
                helperText: 'Valid phone number (10-15 digits)'
              })}
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={3} justifyContent="center">
            <Grid item xs={12}>
              <Typography variant="h6" color="primary" gutterBottom>
                Photo Capture
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Please capture a clear passport photograph
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6} md={5}>
              <Paper elevation={3} sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                  Camera Preview
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <Webcam
                    audio={false}
                    ref={webcamRef}
                    screenshotFormat="image/jpeg"
                    videoConstraints={{ width: 320, height: 360, facingMode: 'user' }}
                    style={{
                      borderRadius: '12px',
                      border: '2px solid #e0e0e0',
                      width: '100%',
                      maxWidth: '320px',
                      height: 'auto',
                      objectFit: 'cover',
                    }}
                  />
                </Box>
                <Button
                  variant="contained"
                  startIcon={<PhotoCamera />}
                  onClick={capture}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 'bold',
                    borderRadius: 2,
                    backgroundColor: '#C84646',
                    '&:hover': { backgroundColor: '#b33c3c' },
                  }}
                >
                  Capture Photo
                </Button>
              </Paper>
            </Grid>
            
            <Grid item xs={12} sm={6} md={5}>
              <Paper elevation={3} sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                  Captured Photo
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  {imageSrc ? (
                    <img
                      src={imageSrc}
                      alt="Passport"
                      style={{
                        width: '100%',
                        maxWidth: '320px',
                        height: 'auto',
                        borderRadius: '12px',
                        border: '2px solid #e0e0e0',
                        objectFit: 'cover',
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        maxWidth: '320px',
                        height: '240px',
                        borderRadius: '12px',
                        border: '2px dashed #e0e0e0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f5f5f5',
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No photo captured yet
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<Replay />}
                  onClick={() => setImageSrc(null)}
                  disabled={!imageSrc}
                  sx={{ textTransform: 'none' }}
                >
                  Reset Photo
                </Button>
              </Paper>
            </Grid>
          </Grid>
        );

      case 3:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" color="primary" gutterBottom>
                Educational Background
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Please provide your educational history (Only Primary School is required)
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              {renderTextField('education', 'primary_school_name', 'Primary School Name', 'text', true)}
            </Grid>
            
            <Grid item xs={12} md={6}>
              {renderTextField('education', 'primary_school_address', 'Primary School Address', 'text', true)}
            </Grid>
            
            <Grid item xs={12} md={6}>
              {renderTextField('education', 'secondary_school_name', 'Secondary School Name', 'text', false, {
                helperText: 'Optional'
              })}
            </Grid>
            
            <Grid item xs={12} md={6}>
              {renderTextField('education', 'secondary_school_address', 'Secondary School Address', 'text', false, {
                helperText: 'Optional'
              })}
            </Grid>
            
            <Grid item xs={12} md={6}>
              {renderTextField('education', 'tertiary_school_name', 'Tertiary Institution Name', 'text', false, {
                helperText: 'University, Polytechnic, or College (Optional)'
              })}
            </Grid>
            
            <Grid item xs={12} md={6}>
              {renderTextField('education', 'tertiary_school_address', 'Tertiary Institution Address', 'text', false, {
                helperText: 'Optional'
              })}
            </Grid>
          </Grid>
        );

    case 4:
  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" color="primary" gutterBottom>
          Work Experience
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Please provide your professional experience details
        </Typography>
      </Grid>
      
      <Grid item xs={12} md={6}>
        {renderSelectField('experience', 'experience_type', 'Experience Type', experienceTypeOptions, true)}
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          type="number"
          label="Years of Experience (Optional)"
          value={formData.experience.years_of_experience}
          onChange={(e) => {
            const value = e.target.value;
            // Allow empty string or numbers >= 0
            if (value === '' || (Number(value) >= 0 && !isNaN(Number(value)))) {
              handleChange('experience', 'years_of_experience', value);
            }
          }}
          onBlur={() => handleFieldBlur('experience', 'years_of_experience')}
          helperText="Optional - Leave empty or enter 0 if no experience"
          InputProps={{ inputProps: { min: 0, step: 0.5 } }}
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        {renderTextField('experience', 'last_workplace_one', 'Last Workplace (Most Recent)', 'text', false, {
          helperText: 'Optional - Leave empty if no experience'
        })}
      </Grid>
      
      <Grid item xs={12} md={6}>
        {renderTextField('experience', 'last_workplace_two', 'Last Workplace (Previous)', 'text', false, {
          helperText: 'Optional'
        })}
      </Grid>
      
      <Grid item xs={12}>
        {renderTextField('experience', 'self_evaluation', 'Self Evaluation', 'text', true, {
          multiline: true,
          rows: 4,
          helperText: 'Required - Write about your skills, technical ability, emotional stability, communication skills, etc.'
        })}
      </Grid>
      
      <Grid item xs={12} md={6}>
        {renderTextField('experience', 'salary_expectation', 'Salary Expectation (₦)', 'number', true, {
          helperText: 'Required - Expected monthly salary in Naira'
        })}
      </Grid>
    </Grid>
  );
      case 5:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" color="primary" gutterBottom>
                Last Employment Details 
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Please provide information about your most recent employer. Leave empty if this is your first job.
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              {renderTextField('employment', 'previous_employer', 'Previous Employer', 'text', false, {
                helperText: 'Optional'
              })}
            </Grid>
            
            <Grid item xs={12} md={6}>
              {renderTextField('employment', 'job_title', 'Job Title', 'text', false, {
                helperText: 'Optional'
              })}
            </Grid>
            
            <Grid item xs={12} md={6}>
              {renderTextField('employment', 'employment_start_date', 'Start Date', 'date', false, {
                InputLabelProps: { shrink: true },
                helperText: 'Optional'
              })}
            </Grid>
            
            <Grid item xs={12} md={6}>
              {renderTextField('employment', 'employment_end_date', 'End Date', 'date', false, {
                InputLabelProps: { shrink: true },
                helperText: 'Optional - Leave empty if currently employed'
              })}
            </Grid>
            
            <Grid item xs={12}>
              {renderTextField('employment', 'reason_for_leaving', 'Reason for Leaving', 'text', false, {
                multiline: true,
                rows: 2,
                helperText: 'Optional'
              })}
            </Grid>
            
            <Grid item xs={12} md={6}>
              {renderTextField('employment', 'employer_contact_name', 'Employer Contact Name', 'text', false, {
                helperText: 'Optional'
              })}
            </Grid>
            
            <Grid item xs={12} md={6}>
              {renderTextField('employment', 'employer_phone', 'Employer Phone', 'tel', false, {
                helperText: 'Optional'
              })}
            </Grid>
          </Grid>
        );

      case 6:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" color="primary" gutterBottom>
                Document Uploads
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Please upload the required documents (Max 5MB per file)
              </Typography>
            </Grid>

            {[
              { name: 'guarantor_upload', label: 'Guarantor Letter', icon: <AssignmentIndIcon />, required: true },
              { name: 'cv_upload', label: 'CV / Resume', icon: <DescriptionIcon />, required: true },
              { name: 'academic_result_upload', label: 'Academic Certificate', icon: <SchoolIcon />, required: true },
              { name: 'id_upload', label: 'Government ID', icon: <BadgeIcon />, required: true },
              { name: 'staff_signature_upload', label: 'Signature', icon: <BorderColorIcon />, required: true }
            ].map((file) => (
              <Grid item xs={12} md={6} lg={4} key={file.name}>
                <Box display="flex" flexDirection="column" alignItems="flex-start">
                  <Button
                    variant="outlined"
                    component="label"
                    fullWidth
                    startIcon={file.icon}
                    sx={{
                      justifyContent: 'flex-start',
                      padding: '12px 16px',
                      textTransform: 'none',
                      fontSize: '1rem',
                      borderColor: !formData.security_and_upload[file.name] && file.required ? '#d32f2f' : undefined,
                    }}
                  >
                    {file.label} {file.required && <span style={{ color: '#d32f2f', marginLeft: 4 }}>*</span>}
                    <input
                      type="file"
                      hidden
                      accept=".jpg,.jpeg,.png,.pdf,.docx"
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          if (e.target.files[0].size > 5 * 1024 * 1024) {
                            showSnackbar(`File ${e.target.files[0].name} exceeds 5MB limit`, 'error');
                            return;
                          }
                          handleChange('security_and_upload', file.name, e.target.files[0]);
                        }
                      }}
                    />
                  </Button>
                  {formData.security_and_upload[file.name] && (
                    <Chip
                      label={formData.security_and_upload[file.name].name}
                      size="small"
                      onDelete={() => handleChange('security_and_upload', file.name, null)}
                      sx={{ mt: 1, maxWidth: '100%' }}
                    />
                  )}
                  {!formData.security_and_upload[file.name] && file.required && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                      Required
                    </Typography>
                  )}
                </Box>
              </Grid>
            ))}

            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 2, mb: 2, color: '#C84646' }}>
                Additional Academic Documents (Optional)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Upload additional certificates (Professional, Diplomas, etc.) - Maximum 3 documents
              </Typography>
            </Grid>

            {formData.security_and_upload.additional_academic_uploads.map((doc) => (
              <Grid item xs={12} key={doc.id}>
                <Paper elevation={2} sx={{ p: 3, mb: 2, position: 'relative' }}>
                  <IconButton
                    size="small"
                    color="error"
                    sx={{ position: 'absolute', top: 8, right: 8 }}
                    onClick={() => removeAdditionalAcademicDocument(doc.id)}
                  >
                    <Delete />
                  </IconButton>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Document Name/Description"
                        fullWidth
                        placeholder="e.g., Professional Certification, Diploma, etc."
                        value={doc.document_name}
                        onChange={(e) => updateAdditionalDocumentName(doc.id, e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Button
                        variant="outlined"
                        component="label"
                        fullWidth
                        startIcon={<DescriptionIcon />}
                        sx={{ height: '56px' }}
                      >
                        Choose File
                        <input
                          type="file"
                          hidden
                          accept=".jpg,.jpeg,.png,.pdf"
                          onChange={(e) => {
                            if (e.target.files && e.target.files[0]) {
                              if (e.target.files[0].size > 5 * 1024 * 1024) {
                                showSnackbar(`File ${e.target.files[0].name} exceeds 5MB limit`, 'error');
                                return;
                              }
                              updateAdditionalDocumentFile(doc.id, e.target.files[0]);
                            }
                          }}
                        />
                      </Button>
                      {doc.file && (
                        <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                          Selected: {doc.file.name}
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            ))}

            <Grid item xs={12}>
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={addAdditionalAcademicDocument}
                disabled={formData.security_and_upload.additional_academic_uploads.length >= 3}
              >
                Add Additional Document
              </Button>
            </Grid>

            <Grid item xs={12}>
              <FormControl component="fieldset">
                <FormLabel component="legend">Have you ever been convicted of a crime?</FormLabel>
                <RadioGroup
                  row
                  value={formData.security_and_upload.convicted ? 'yes' : 'no'}
                  onChange={(e) =>
                    handleChange('security_and_upload', 'convicted', e.target.value === 'yes')
                  }
                >
                  <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                  <FormControlLabel value="no" control={<Radio />} label="No" />
                </RadioGroup>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                <Typography variant="h6" sx={{ mb: 1, color: '#C84646' }}>
                  Security Declaration
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.95rem', color: 'text.secondary' }}>
                  I hereby affirm that all the information provided in this document is true, accurate, 
                  and complete to the best of my knowledge. I acknowledge that providing false, misleading, 
                  or incomplete information, or failing to disclose any required details, may result in 
                  disciplinary action, including but not limited to the termination of my employment.
                </Typography>
              </Box>
              <FormControl component="fieldset" required error={!formData.security_and_upload.security_declaration}>
                <FormLabel component="legend">Do you agree to the security declaration? *</FormLabel>
                <RadioGroup
                  row
                  value={formData.security_and_upload.security_declaration ? 'yes' : 'no'}
                  onChange={(e) =>
                    handleChange('security_and_upload', 'security_declaration', e.target.value === 'yes')
                  }
                >
                  <FormControlLabel value="yes" control={<Radio />} label="Yes, I agree" />
                  <FormControlLabel value="no" control={<Radio />} label="No, I do not agree" />
                </RadioGroup>
                {!formData.security_and_upload.security_declaration && (
                  <FormHelperText error>You must agree to the security declaration to proceed</FormHelperText>
                )}
              </FormControl>
            </Grid>
          </Grid>
        );

      case 7:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" color="primary" gutterBottom>
                Bank Account Information
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Please provide your salary account details for payroll processing
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                <strong>Bank Account Verification</strong><br />
                Enter your 10-digit account number and select your bank to automatically verify your account details.
                The account name must match your registered name.
              </Alert>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Select Bank</InputLabel>
                <Select
                  value={formData.bank.bank_code || ''}
                  label="Select Bank"
                  onChange={(e) => {
                    const selectedBank = banks.find(b => b.code === e.target.value);
                    handleChange('bank', 'bank_code', e.target.value);
                    handleChange('bank', 'bank_name', selectedBank?.name || '');
                    handleChange('bank', 'account_verified', false);
                    handleChange('bank', 'account_name', '');
                  }}
                  sx={{ minWidth: 150 }}
                >
                  <MenuItem value="">Select Bank</MenuItem>
                  {banks.map((bank) => (
                    <MenuItem key={bank.code} value={bank.code}>
                      {bank.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Account Number"
                fullWidth
                required
                value={formData.bank.account_number}
                onChange={async (e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                  handleChange('bank', 'account_number', value);
                  handleChange('bank', 'account_verified', false);
                  handleChange('bank', 'account_name', '');
                  
                  if (value.length === 10 && formData.bank.bank_code) {
                    setVerifyingAccount(true);
                    try {
                      const phone = localStorage.getItem('phoneNumber');
                      const response = await axios.get(
                        `${process.env.REACT_APP_API_BASE_URL}/hotel/HR/verify_bank_account.php`,
                        {
                          params: {
                            account_number: value,
                            bank_code: formData.bank.bank_code,
                            phone: phone
                          }
                        }
                      );
                      
                      if (response.data.success) {
                        handleChange('bank', 'account_name', response.data.data.account_name);
                        handleChange('bank', 'account_verified', true);
                        showSnackbar(`Account verified: ${response.data.data.account_name}`, 'success');
                      } else {
                        showSnackbar(response.data.message || 'Account verification failed', 'error');
                      }
                    } catch (err) {
                      console.error('Verification error:', err);
                      
                      if (err.response && err.response.status === 409) {
                        const errorData = err.response.data;
                        setErrorDialog({
                          open: true,
                          title: 'Account Name Mismatch',
                          message: errorData.message,
                          details: [
                            `Staff Name: ${errorData.data?.staff_name || 'Unknown'}`,
                            `Account Name: ${errorData.data?.verified_account_name || 'Unknown'}`,
                            `Account Number: ${errorData.data?.account_number || value}`,
                            'The account name must match the staff name to proceed.'
                          ],
                          fieldErrors: {}
                        });
                        handleChange('bank', 'account_verified', false);
                      } else {
                        showSnackbar(err.response?.data?.message || 'Failed to verify account', 'error');
                      }
                    } finally {
                      setVerifyingAccount(false);
                    }
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {verifyingAccount ? (
                        <CircularProgress size={20} />
                      ) : formData.bank.account_verified ? (
                        <VerifiedIcon sx={{ color: '#4caf50' }} />
                      ) : (
                        <AccountBalanceIcon sx={{ color: '#C84646' }} />
                      )}
                    </InputAdornment>
                  ),
                }}
                helperText={
                  formData.bank.account_number?.length === 10 && !formData.bank.account_verified && !verifyingAccount
                    ? "Account not verified or name mismatch. Please check."
                    : "Enter 10-digit account number"
                }
                error={
                  formData.bank.account_number?.length === 10 && 
                  !formData.bank.account_verified && 
                  !verifyingAccount
                }
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Account Name"
                fullWidth
                value={formData.bank.account_name}
                disabled
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ color: '#C84646' }} />
                    </InputAdornment>
                  ),
                }}
                helperText={formData.bank.account_verified ? "✓ Verified account name" : "Will auto-populate after verification"}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Bank Name"
                fullWidth
                value={formData.bank.bank_name}
                disabled
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BusinessIcon sx={{ color: '#C84646' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            {formData.bank.account_verified && (
              <Grid item xs={12}>
                <Alert severity="success" icon={<VerifiedIcon />} sx={{ borderRadius: 2 }}>
                  <strong>Account Verified!</strong> Your account has been successfully verified.
                </Alert>
              </Grid>
            )}
          </Grid>
        );
        
      default:
        return <Typography>Unknown step</Typography>;
    }
  };

  const isNextDisabled = () => {
    if (loading) return true;
    if (activeStep === 6 && !formData.security_and_upload.security_declaration) return true;
    if (activeStep === 7 && !formData.bank.account_verified) return true;
    return false;
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
          mt: 0,
          maxWidth: '1400px',
          mx: 'auto',
          overflow: 'auto',
          height: '100vh',
        }}
      >
        <TopNav
          onLogout={() => {
            localStorage.removeItem('authToken');
            localStorage.removeItem('userProfile');
            navigate('/Entri');
          }}
          onNotificationClick={() => showSnackbar('You have new notifications!', 'info')}
          toggleSidebar={toggleSidebar}
        />

      

        <Grid container spacing={3} sx={{ mt: 3, mb: 3 }}>
          <Grid item xs={12}>
            <Paper
              elevation={4}
              sx={{
                p: { xs: 2, sm: 3, md: 4 },
                width: '100%',
                borderRadius: 4,
                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)'
              }}
            >
              <Typography
                variant="h5"
                component="h2"
                align="left"
                sx={{
                  fontWeight: 600,
                  mb: 1,
                  color: '#C84646',
                }}
              >
                Staff Onboarding
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Complete all steps to successfully onboard a new staff member
              </Typography>
              
              {userData && (
                <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                  <strong>Onboarding for:</strong> {userData.first_name} {userData.other_names} ({userData.email})
                </Alert>
              )}
              
              <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4, overflowX: 'auto' }}>
                {steps.map((label, index) => (
                  <Step key={label} completed={completed[index]}>
                    <StepLabel
                      StepIconProps={{
                        onClick: () => handleStep(index),
                        style: { cursor: 'pointer' }
                      }}
                    >
                      {label}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>

              <Box sx={{ mt: 4 }}>
                {renderStepContent(activeStep)}
              </Box>

              <Box
                sx={{
                  mt: 5,
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                }}
              >
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={isNextDisabled()}
                  sx={{
                    px: 6,
                    py: 1.5,
                    fontWeight: 600,
                    fontSize: '16px',
                    borderRadius: 2,
                    backgroundColor: '#C84646',
                    '&:hover': {
                      backgroundColor: '#b33c3c',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: '#cccccc',
                    }
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    activeStep === steps.length - 1 ? 'Submit & Complete' : 'Continue'
                  )}
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog open={errorDialog.open} onClose={handleCloseErrorDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#C84646', color: 'white' }}>
          {errorDialog.title || 'Submission Error'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {errorDialog.message || 'Please correct the following issues:'}
          </Typography>
          
          {errorDialog.details && errorDialog.details.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom color="error">
                Details:
              </Typography>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                {errorDialog.details.map((detail, index) => (
                  <li key={index} style={{ marginBottom: '4px' }}>{detail}</li>
                ))}
              </ul>
            </Box>
          )}
          
          {errorDialog.fieldErrors && Object.keys(errorDialog.fieldErrors).length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom color="error">
                Please fix these fields:
              </Typography>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                {Object.entries(errorDialog.fieldErrors).map(([field, error]) => (
                  <li key={field} style={{ marginBottom: '4px' }}>
                    <strong>{field.replace(/_/g, ' ').toUpperCase()}:</strong> {error}
                  </li>
                ))}
              </ul>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseErrorDialog} variant="contained" sx={{ bgcolor: '#C84646' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HRFormDashboard;